import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, Subscription, take } from 'rxjs';

import { ElementActions, ElementTypes, GaContext, SelectOption, ToasterService, ValidationErrors } from '@fcom/common';
import { FinnairEmergencyContact, FinnairPhoneType } from '@fcom/dapi/api/models';
import { ButtonSize, ButtonTheme, ButtonType, IconPosition, NotificationTheme } from '@fcom/ui-components';
import { NAME_PATTERN, PHONE_PATTERN, PHONE_PREFIX_PATTERN, unsubscribe } from '@fcom/core/utils';
import { PhonePrefixService } from '@fcom/common/services/phone-prefix/phone-prefix.service';
import { GtmService } from '@fcom/common/gtm';
import { TypedFormGroup } from '@fcom/service-forms';

import { ManageBookingService } from '../../../../services';

const phoneValidation = (formGroup: UntypedFormGroup): ValidationErrors => {
  const phonePrefix = formGroup.get('phonePrefix');
  const phoneNumber = formGroup.get('phoneNumber');

  const phonePrefixValidation = Validators.compose([Validators.required, Validators.pattern(PHONE_PREFIX_PATTERN)])(
    phonePrefix
  );
  const phoneNumberValidation = Validators.compose([Validators.required, Validators.pattern(PHONE_PATTERN)])(
    phoneNumber
  );

  phonePrefix.setErrors(phonePrefixValidation);
  phoneNumber.setErrors(phoneNumberValidation);
  return null;
};

@Component({
  selector: 'fin-mmb-emergency-contact',
  templateUrl: './emergency-contact.component.html',
  styleUrls: ['../../../../../common-booking/assets/helper-classes.scss', './emergency-contact.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingEmergencyContactComponent implements OnInit, OnDestroy {
  readonly ButtonType = ButtonType;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  private subscriptions: Subscription = new Subscription();

  @Input() trackApis = false;
  @Input() emergencyContact: FinnairEmergencyContact;
  @Output() formDirtyStatus: EventEmitter<boolean> = new EventEmitter();

  showFormView = false;
  updatingContacts = false;
  contactForm: TypedFormGroup;
  phonePrefixes$: Observable<SelectOption[]>;

  constructor(
    private gtmService: GtmService,
    private fb: UntypedFormBuilder,
    private phonePrefixService: PhonePrefixService,
    private mmbService: ManageBookingService,
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    const defaults = {
      firstName: this.emergencyContact?.contactName?.split(/\s/)[0] ?? '',
      lastName: this.emergencyContact?.contactName?.split(/\s/).slice(1).join(' ') ?? '',
      phonePrefix:
        this.emergencyContact?.phoneNumberItem?.countryCode && this.emergencyContact?.phoneNumberItem?.countryPrefix
          ? `${this.emergencyContact?.phoneNumberItem?.countryCode}|${this.emergencyContact?.phoneNumberItem?.countryPrefix}`
          : '',
      phoneNumber: this.emergencyContact?.phoneNumberItem
        ? this.emergencyContact?.phoneNumberItem?.number.replace(/\s/, '')
        : '',
    };

    this.contactForm = this.fb.group({
      firstName: [
        defaults.firstName,
        [Validators.required, Validators.maxLength(50), Validators.pattern(NAME_PATTERN)],
      ],
      lastName: [defaults.lastName, [Validators.required, Validators.maxLength(50), Validators.pattern(NAME_PATTERN)]],
      phone: this.fb.group(
        {
          phonePrefix: [defaults.phonePrefix, [Validators.required, Validators.pattern(PHONE_PREFIX_PATTERN)]],
          phoneNumber: [defaults.phoneNumber, [Validators.required, Validators.pattern(PHONE_PATTERN)]],
        },
        { validators: phoneValidation }
      ),
    }) as TypedFormGroup;

    this.phonePrefixes$ = this.phonePrefixService.setupPhonePrefixes(false).pipe(take(1));
    this.subscriptions.add(
      this.contactForm.valueChanges.subscribe(() => this.formDirtyStatus.emit(this.contactForm.dirty))
    );
  }

  toggleEditView(show: boolean, skipTracking = false): void {
    this.showFormView = show;
    if (!show) {
      this.formDirtyStatus.emit(false);
    }
    if (!skipTracking) {
      this.gtmService.trackElement(
        `${this.trackApis ? 'apis-' : ''}update-details_emergencyContact-${show ? 'edit' : 'cancel'}`,
        GaContext.MANAGE_BOOKING,
        ElementTypes.BUTTON,
        undefined,
        ElementActions.CLICK
      );
    }
  }

  stripSpaces(fieldRef: string): void {
    const formField = this.contactForm.get(fieldRef);
    formField.setValue(formField.value.replace(/\s\s/g, ' '));
  }

  updateContact(): void {
    if (this.contactForm.disabled) {
      return;
    }

    if (!this.contactForm.valid) {
      this.contactForm.markAllAsTouched();
      this.contactForm.updateValueAndValidity();
      this.cdr.markForCheck();

      return;
    }

    this.contactForm.disable();
    this.updatingContacts = true;
    this.cdr.markForCheck();
    this.subscriptions.add(
      this.mmbService
        .updateEmergencyContactDetails({
          firstName: this.contactForm.get('firstName').value,
          lastName: this.contactForm.get('lastName').value,
          phoneNumber: {
            type: FinnairPhoneType.EMERGENCY,
            number: this.contactForm.get('phone.phoneNumber').value,
            countryCode: this.contactForm.get('phone.phonePrefix').value.split('|')[0],
            countryPrefix: this.contactForm.get('phone.phonePrefix').value.split('|')[1],
          },
        })
        .subscribe({
          next: () => {
            this.enableEditing('success');
            this.formDirtyStatus.emit(false);
          },
          error: () => {
            this.toasterService.addMessageToQueue({
              id: 'emergency-update-failed',
              contentKey: 'MMB.errors.errorEmergencyContact',
              theme: NotificationTheme.ALERT,
            });

            this.enableEditing('error');
          },
        })
    );
  }

  private enableEditing(type: string): void {
    this.toggleEditView(false, true);
    this.contactForm.enable();
    this.updatingContacts = false;

    this.gtmService.trackElement(
      `${this.trackApis ? 'apis-' : ''}update-details_emergencyContact-${type}`,
      GaContext.MANAGE_BOOKING,
      ElementTypes.FORM,
      undefined,
      ElementActions.SUBMIT
    );

    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }
}
